import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReceiverTeam } from '../../../models/receiver-team';
import { LocationModel } from '../../../models/location.model';
import { StaffModel } from '../../../models/staff.model';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { ThrottleButtonClickDirective } from '../../../directives/throttle-button-click.directive';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { IRoleSymbols } from 'app/models/permission.model';
import { NO_ROLE_SYMBOLS, staffListIconClasses } from 'app/shared/role-symbol/role-symbol';
import { StaffSearch, allShownSelected, toggleShownSelection } from 'app/shared/role-symbol/staff-search';
import { StaffFilterBarComponent } from 'app/shared/role-symbol/staff-filter-bar.component';
import { NgClass } from '@angular/common';


@Component({
    selector: 'team-add',
    templateUrl: './team-add.component.html',
    styleUrls: ['./team-add.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [NgClass, FormsModule, ReactiveFormsModule, MatSlideToggleModule, MatTooltipModule, MatFormFieldModule, MatSelectModule, MatOptionModule, MatCheckboxModule, MatListModule, ThrottleButtonClickDirective, TranslateModule, StaffFilterBarComponent]
})
export class TeamAddComponent implements OnChanges {
  @Input('staff') allStaff: StaffModel[];
  @Output() closeAddFormEvent = new EventEmitter<void>();
  @Output() teamAdded = new EventEmitter<ReceiverTeam>();

  /** Custom roles and who has them, for the staff role symbols and the role filter */
  @Input() roleSymbols: IRoleSymbols = NO_ROLE_SYMBOLS;

  /** Search and role filter of the staff list, shared with the alarm route recipient picker (ES-7564) */
  readonly search = new StaffSearch(() => this.roleSymbols, () => this.currentUserLevel(), this.translate);

  constructor(private api: ApiService, private translate: TranslateService, private fb: UntypedFormBuilder,) {
  }


  // Used to delete Mobile Device location from location list
  private mobileDevicesLocationServerName: string = 'Mobile devices';

  team: ReceiverTeam;
  teamAddForm: UntypedFormGroup;
  locationList: LocationModel[];
  addTeamFormError: string;
  availableStaffForCompany: StaffModel[];
  availableStaffForLocation: StaffModel[];

  createTeamAddForm(): void {
    this.teamAddForm = this.fb.group({
      groupName: ['', {
        validators: [Validators.required],
      }],
      locationName: [''],
      selectedTeamMembers: "",
      teamLocked: false
    })
  }


  getLocations(): void {
    // A failed location list still opens the form, with the customer level only
    this.api.getLocations().subscribe({ next: res => this.setLocations(res ?? []), error: () => this.setLocations([]) });
  }

  private setLocations(locations: LocationModel[]): void {
    //Delete mobile device location from locations
    const mobileDeviceLocationIndex: number = locations.findIndex((location) => location.name === this.mobileDevicesLocationServerName);
    if (mobileDeviceLocationIndex !== -1) {
      locations.splice(mobileDeviceLocationIndex, 1);
    }
    this.locationList = locations;
  }


  closeAddForm(): void {
    this.closeAddFormEvent.emit();
  }

  addTeam(): void {
    let locationId: string = '';
    const selectedLocation: LocationModel = this.locationList.find((locationName) =>
      locationName === this.teamAddForm.value.locationName);

    if (selectedLocation) {
      locationId = selectedLocation.id;
    }

    const groupData = {
      'groupName': this.teamAddForm.value.groupName,
      'locationId': locationId,
      'locked': this.teamAddForm.value.teamLocked,
      'staffs': this.teamAddForm.value.selectedTeamMembers
    };

    this.api.addReceiverTeam(groupData).subscribe(res => {
      this.team = res;
      this.teamAdded.emit(this.team);
      this.closeAddForm();
    }, err => {
      const backendMessage = err?.error?.errors?.[0];
      if (backendMessage) {
        this.addTeamFormError = this.translate.instant(backendMessage);
      } else {
        this.translate.get(['ERROR']).subscribe(t => {
          this.addTeamFormError = t.ERROR;
        });
      }
    })

  }
  parseStaffList(staff: StaffModel[]): void {
    if (this.teamAddForm && this.teamAddForm.value.locationName) {
      this.availableStaffForLocation = (staff ?? []).filter(staff => staff.locationId === this.teamAddForm.value.locationName.id);
    } else {
      this.availableStaffForLocation = [];
    }
    this.availableStaffForCompany = (staff ?? []).filter(staff => staff.locationId?.startsWith('C0'))
  }

  ngOnChanges(changes?: SimpleChanges) {
    // New role symbols only change the icons and the role filter: keep the form as the user has it
    if (changes && Object.keys(changes).every(key => key === 'roleSymbols')) return;
    this.parseStaffList(this.allStaff)
    this.getLocations();
    // Build the form only once. ngOnChanges also fires when the async-loaded
    // `allStaff` input arrives, and rebuilding the form here would wipe values
    // the user has already typed (e.g. the group name).
    if (!this.teamAddForm) {
      this.createTeamAddForm();
    }
  }

  private currentUserLevel(): number | undefined {
    try {
      return JSON.parse(localStorage.getItem('user'))?.roleLevel;
    } catch {
      return undefined;
    }
  }

  /** Staff icon: role symbol, shield for a login identity (same as Staff Management) */
  staffIcon(staff: StaffModel): string {
    return `${staffListIconClasses(staff, this.roleSymbols)} fa-fw`;
  }

  /**
   * Is the staff member shown with the search and role filter. Hidden ones stay in the list (hidden
   * with CSS): removing an option from the selection list would unselect it, and so drop a member.
   */
  isShown(staff: StaffModel): boolean {
    return !this.search.active || this.search.matches({
      id: staff.id, name: staff.fullName, gsmNumber: staff.gsmNumber, level: staff.usingLevel
    });
  }

  shownStaff(staff: StaffModel[] | null | undefined): StaffModel[] {
    return (staff ?? []).filter(s => this.isShown(s));
  }

  private shownStaffIds(): string[] {
    return [...this.shownStaff(this.availableStaffForCompany), ...this.shownStaff(this.availableStaffForLocation)].map(s => s.id);
  }

  allShownSelected(): boolean {
    return allShownSelected(this.teamAddForm.controls.selectedTeamMembers.value, this.shownStaffIds());
  }

  /** Select all: selects the staff the search shows, or unselects them when all are selected */
  toggleAllSelection(): void {
    const control = this.teamAddForm.controls.selectedTeamMembers;
    control.patchValue(toggleShownSelection(control.value, this.shownStaffIds()));
    control.markAsDirty();
  }

}
